import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, getDocs, addDoc, updateDoc, query, orderBy, where, doc, writeBatch
} from 'firebase/firestore';
import * as XLSX from 'xlsx';
import {
  Box, Container, Typography, Paper, Button, TextField, IconButton,
  Chip, CircularProgress, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem, Tooltip, Table,
  TableHead, TableRow, TableCell, TableBody, LinearProgress, Alert,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import { deptCode } from '../utils/depts';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StarIcon from '@mui/icons-material/Star';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

const STATUS_META = {
  offertfas: { label: 'Offertfas', color: '#E65100', bg: '#FFF3E0' },
  aktiv:     { label: 'Aktivt',    color: '#1B5E20', bg: '#E8F5E9' },
  pausad:    { label: 'Pausat',    color: '#4A148C', bg: '#F3E5F5' },
  avslutad:  { label: 'Avslutat',  color: '#37474F', bg: '#ECEFF1' },
};

const EMPTY_FORM = {
  name: '', projectNumber: '', clientId: '', status: 'offertfas',
  ownerId: '', startDate: '', endDate: '', departmentIds: [], description: '',
};

// Parse Milltime Excel/CSV and return array of row objects.
// Robust against varying header-row position (some exports have a title row
// above the headers, others start with the headers) and changed column order.
function parseMilltimeXlsx(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });

        const norm = v => String(v || '').toLowerCase().trim();

        // Detect the header row: the first row (within the first ~10) that
        // contains at least two recognised column names.
        const KNOWN = [
          'projektnummer', 'nummer', 'nr',
          'projekt', 'projektnamn', 'namn',
          'kund', 'kundnamn', 'bolag',
          'projektledare', 'projektägare', 'ägare', 'ansvarig',
          'resultatenhet', 'projekttyp', 'typ', 'budget', 'inaktiv',
        ];
        let headerRowIdx = -1;
        for (let i = 0; i < Math.min(rows.length, 10); i++) {
          const cells = (rows[i] || []).map(norm);
          const hits = cells.filter(c => KNOWN.includes(c)).length;
          if (hits >= 2) { headerRowIdx = i; break; }
        }
        if (headerRowIdx === -1) {
          reject(new Error(
            'Kunde inte hitta kolumnrubrikerna. Filen måste innehålla en rad med ' +
            'rubrikerna Projektnummer, Projekt, Kund, Projektledare, Resultatenhet.'
          ));
          return;
        }

        const headers = (rows[headerRowIdx] || []).map(norm);

        // Find column index by trying multiple known header names. Returns -1 if none found.
        function col(...names) {
          for (const name of names) {
            const idx = headers.indexOf(name.toLowerCase());
            if (idx >= 0) return idx;
          }
          return -1;
        }

        // Map purely by header name — no positional fallback, since column
        // order varies between exports.
        const numIdx    = col('projektnummer', 'nummer', 'nr');
        const nameIdx   = col('projekt', 'projektnamn', 'namn');
        const budgetIdx = col('budget', 'budgetbelopp');
        const kundIdx   = col('kund', 'kundnamn', 'bolag');
        const ownerIdx  = col('projektledare', 'projektägare', 'ägare', 'ansvarig');
        const reIdx     = col('resultatenhet');
        const typIdx    = col('projekttyp', 'typ');
        const inakIdx   = col('inaktiv');

        const at = (r, idx) => (idx >= 0 ? r[idx] : undefined);

        // Milltime exports booleans as Swedish SANT/FALSKT (or true/Ja).
        const isTrue = v => {
          if (v === true) return true;
          const s = norm(v);
          return s === 'sant' || s === 'true' || s === 'ja' || s === '1';
        };

        const result = [];
        for (let i = headerRowIdx + 1; i < rows.length; i++) {
          const r = rows[i];
          if (!r || !at(r, numIdx)) continue;
          result.push({
            projectNumber: String(at(r, numIdx)    || '').trim(),
            name:          String(at(r, nameIdx)   || '').trim(),
            budget:        at(r, budgetIdx) || '',
            clientName:    String(at(r, kundIdx)   || '').trim(),
            ownerName:     String(at(r, ownerIdx)  || '').trim(),
            resultatenhet: String(at(r, reIdx)     || '').trim(),
            projekttyp:    String(at(r, typIdx)    || '').trim(),
            inaktiv:       isTrue(at(r, inakIdx)),
          });
        }
        resolve(result);
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

export default function ProjectList() {
  const { currentUser, userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'superadmin' || userProfile?.role === 'vd' || userProfile?.isAdmin === true;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterNumber, setFilterNumber] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [sortField, setSortField] = useState('projectNumber'); // 'projectNumber' | 'name' | 'createdAt' | 'isReferenceProject'
  const [sortDir, setSortDir] = useState('asc');
  const [filterRef, setFilterRef] = useState(''); // '' | 'ja' | 'nej'

  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [newCompanyDialog, setNewCompanyDialog] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  // Import state
  const [importDialog, setImportDialog] = useState(false);
  const [importStep, setImportStep] = useState(0); // 0=pick, 1=preview, 2=importing, 3=done
  const [importRows, setImportRows] = useState([]);  // parsed + enriched rows
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [importFilter, setImportFilter] = useState('all'); // 'all' | 'new' | 'exists'
  const [importUpdateExisting, setImportUpdateExisting] = useState(false);
  const [importError, setImportError] = useState('');

  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Admin select/delete mode
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleteSelectedDialog, setDeleteSelectedDialog] = useState(false);
  const [deletingSelected, setDeletingSelected] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [projSnap, compSnap, userSnap, deptSnap] = await Promise.all([
      getDocs(query(collection(db, 'projects'), orderBy('createdAt', 'desc'))),
      getDocs(query(collection(db, 'crmCompanies'), orderBy('name'))),
      getDocs(collection(db, 'users')),
      getDocs(collection(db, 'departments')),
    ]);
    setProjects(projSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setCompanies(compSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setUsers(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setDepartments(deptSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (searchParams.get('new') === '1') setDialog(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleCreate() {
    if (!form.name.trim()) return;
    setSaving(true);
    const ref = await addDoc(collection(db, 'projects'), {
      ...form,
      ownerId: form.ownerId || currentUser.uid,
      createdBy: currentUser.uid,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    setSaving(false);
    setDialog(false);
    setForm(EMPTY_FORM);
    navigate(`/projects/${ref.id}`);
  }

  async function handleCreateCompany() {
    if (!newCompanyName.trim()) return;
    const docRef = await addDoc(collection(db, 'crmCompanies'), {
      name: newCompanyName.trim(), createdAt: new Date(),
    });
    const newComp = { id: docRef.id, name: newCompanyName.trim() };
    setCompanies(prev => [...prev, newComp].sort((a, b) => a.name.localeCompare(b.name)));
    setForm(f => ({ ...f, clientId: docRef.id }));
    setNewCompanyName('');
    setNewCompanyDialog(false);
  }

  async function handleBulkDeleteAvslutade() {
    setBulkDeleting(true);
    try {
      const toDelete = projects.filter(p => p.status === 'avslutad');
      // writeBatch has a limit of 500 ops — chunk if needed
      const CHUNK = 400;
      for (let i = 0; i < toDelete.length; i += CHUNK) {
        const batch = writeBatch(db);
        toDelete.slice(i, i + CHUNK).forEach(p => batch.delete(doc(db, 'projects', p.id)));
        await batch.commit();
      }
      setProjects(prev => prev.filter(p => p.status !== 'avslutad'));
      setBulkDeleteDialog(false);
    } finally {
      setBulkDeleting(false);
    }
  }

  function toggleSelectMode() {
    setSelectMode(prev => {
      if (prev) setSelectedIds(new Set()); // clear on exit
      return !prev;
    });
  }

  function toggleSelected(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleDeleteSelected() {
    setDeletingSelected(true);
    try {
      const ids = [...selectedIds];
      const CHUNK = 400; // writeBatch limit is 500
      for (let i = 0; i < ids.length; i += CHUNK) {
        const batch = writeBatch(db);
        ids.slice(i, i + CHUNK).forEach(id => batch.delete(doc(db, 'projects', id)));
        await batch.commit();
      }
      setProjects(prev => prev.filter(p => !selectedIds.has(p.id)));
      setSelectedIds(new Set());
      setSelectMode(false);
      setDeleteSelectedDialog(false);
    } finally {
      setDeletingSelected(false);
    }
  }

  // ── Import handlers ──────────────────────────────────────────────────────

  function openImport() {
    setImportStep(0);
    setImportRows([]);
    setImportResult(null);
    setImportFilter('all');
    setImportError('');
    setImportDialog(true);
  }

  async function handleImportFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    let parsed;
    try {
      parsed = await parseMilltimeXlsx(file);
    } catch (err) {
      setImportError(err.message || 'Kunde inte läsa filen.');
      return;
    }

    // Enrich each row with match info
    const existingNrs = new Set(projects.map(p => String(p.projectNumber || '').trim()));
    const compByName = {};
    companies.forEach(c => { compByName[c.name.toLowerCase()] = c; });
    const deptByCode = {};
    departments.forEach(d => {
      // Match on name directly (BTM, MEK, IV etc.) or abbreviation field
      const key = (d.abbreviation || d.name || '').toUpperCase();
      deptByCode[key] = d;
      // Also try first word of name
      const firstWord = (d.name || '').split(' ')[0].toUpperCase();
      if (firstWord) deptByCode[firstWord] = d;
    });
    const userByName = {};
    users.forEach(u => {
      const n = (u.displayName || u.email || '').toLowerCase();
      userByName[n] = u;
    });

    const enriched = parsed.map(r => {
      const exists = existingNrs.has(r.projectNumber);
      const matchedCompany = r.clientName ? compByName[r.clientName.toLowerCase()] : null;
      const matchedDept = deptByCode[r.resultatenhet.toUpperCase()] || null;
      const matchedUser = userByName[(r.ownerName || '').toLowerCase()] || null;
      return {
        ...r,
        exists,
        matchedCompany,
        matchedDept,
        matchedUser,
        include: !exists, // default: import new, skip existing
      };
    });

    setImportRows(enriched);
    setImportStep(1);
  }

  function toggleInclude(idx) {
    setImportRows(prev => prev.map((r, i) => i === idx ? { ...r, include: !r.include } : r));
  }

  function toggleUpdateExisting() {
    setImportUpdateExisting(prev => {
      const next = !prev;
      setImportRows(rows => rows.map(r => r.exists ? { ...r, include: next } : r));
      return next;
    });
  }

  async function handleExecuteImport() {
    const toImport = importRows.filter(r => r.include);
    if (toImport.length === 0) return;

    setImportStep(2);
    setImportProgress(0);

    // Collect unique unknown company names that need to be created
    const newCompanyNames = [...new Set(
      toImport
        .filter(r => r.clientName && !r.matchedCompany)
        .map(r => r.clientName)
    )];

    // Create missing companies
    const createdCompanies = {};
    for (const name of newCompanyNames) {
      const docRef = await addDoc(collection(db, 'crmCompanies'), {
        name, createdAt: new Date(),
      });
      createdCompanies[name] = docRef.id;
    }

    let created = 0;
    let updated = 0;
    let skipped = 0;

    for (let i = 0; i < toImport.length; i++) {
      const r = toImport[i];
      setImportProgress(Math.round(((i + 1) / toImport.length) * 100));

      // Resolve clientId
      let clientId = r.matchedCompany?.id || createdCompanies[r.clientName] || '';

      // Resolve departmentIds
      const departmentIds = r.matchedDept ? [r.matchedDept.id] : [];

      // Resolve ownerId
      const ownerId = r.matchedUser?.id || currentUser.uid;

      // Check if project already exists in Firestore
      const snap = await getDocs(
        query(collection(db, 'projects'), where('projectNumber', '==', r.projectNumber))
      );

      if (!snap.empty) {
        if (importUpdateExisting) {
          await updateDoc(snap.docs[0].ref, {
            name: r.name,
            clientId,
            departmentIds,
            ownerId,
            status: r.inaktiv ? 'avslutad' : 'aktiv',
            milltimeResultatenhet: r.resultatenhet,
            milltimeProjekttyp: r.projekttyp,
            updatedAt: new Date(),
          });
          updated++;
        } else {
          skipped++;
        }
        continue;
      }

      await addDoc(collection(db, 'projects'), {
        name: r.name,
        projectNumber: r.projectNumber,
        clientId,
        departmentIds,
        ownerId,
        status: r.inaktiv ? 'avslutad' : 'aktiv',
        milltimeResultatenhet: r.resultatenhet,
        milltimeProjekttyp: r.projekttyp,
        createdBy: currentUser.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
        importedFromMilltime: true,
      });
      created++;
    }

    setImportResult({
      created,
      updated,
      skipped,
      newCompanies: newCompanyNames.length,
    });
    setImportStep(3);
    await load(); // refresh list
  }

  // ── Filtered list ────────────────────────────────────────────────────────

  const filtered = projects
    .filter(p => {
      const q = search.toLowerCase();
      const company = companies.find(c => c.id === p.clientId);
      const matchSearch = !q ||
        p.name?.toLowerCase().includes(q) ||
        p.projectNumber?.toLowerCase().includes(q) ||
        company?.name?.toLowerCase().includes(q);
      const matchNumber = !filterNumber ||
        String(p.projectNumber || '').toLowerCase().includes(filterNumber.toLowerCase());
      const matchStatus = !filterStatus || p.status === filterStatus;
      const matchDept = !filterDept || (p.departmentIds || []).includes(filterDept);
      const matchRef = !filterRef || (filterRef === 'ja' ? !!p.isReferenceProject : !p.isReferenceProject);
      return matchSearch && matchNumber && matchStatus && matchDept && matchRef;
    })
    .sort((a, b) => {
      let av, bv;
      if (sortField === 'projectNumber') {
        // Natural sort for numbers like 1527-7013
        av = String(a.projectNumber || '');
        bv = String(b.projectNumber || '');
        const cmp = av.localeCompare(bv, 'sv', { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      }
      if (sortField === 'name') {
        av = (a.name || '').toLowerCase();
        bv = (b.name || '').toLowerCase();
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (sortField === 'isReferenceProject') {
        av = a.isReferenceProject ? 1 : 0;
        bv = b.isReferenceProject ? 1 : 0;
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      // createdAt (default Firestore order)
      av = a.createdAt?.toMillis?.() ?? 0;
      bv = b.createdAt?.toMillis?.() ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  function handleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  function SortIcon({ field }) {
    if (sortField !== field) return null;
    return sortDir === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 14, ml: 0.3 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 14, ml: 0.3 }} />;
  }

  const totalCount = projects.length;
  const activeCount = projects.filter(p => p.status === 'aktiv').length;
  const offertCount = projects.filter(p => p.status === 'offertfas').length;

  // Import preview filter
  const previewRows = importRows.filter(r => {
    if (importFilter === 'new') return !r.exists;
    if (importFilter === 'exists') return r.exists;
    return true;
  });
  const newCount = importRows.filter(r => !r.exists).length;
  const existsCount = importRows.filter(r => r.exists).length;
  const includedCount = importRows.filter(r => r.include).length;
  const unknownCompanies = [...new Set(
    importRows.filter(r => r.include && r.clientName && !r.matchedCompany).map(r => r.clientName)
  )];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">Uppdrag</Typography>
            <Typography variant="caption" sx={{ color: '#aaa' }}>
              {totalCount} uppdrag totalt
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdmin && (
            <Button variant="outlined"
              startIcon={selectMode ? <CloseIcon /> : <CheckBoxOutlinedIcon />}
              onClick={toggleSelectMode}
              sx={{ color: selectMode ? '#FF6B00' : 'white',
                borderColor: selectMode ? '#FF6B00' : 'rgba(255,255,255,0.4)',
                '&:hover': { borderColor: selectMode ? '#FF6B00' : 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              {selectMode ? 'Avbryt markering' : 'Markera'}
            </Button>
          )}
          {projects.some(p => p.status === 'avslutad') && (
            <Button variant="outlined" startIcon={<DeleteSweepIcon />}
              onClick={() => setBulkDeleteDialog(true)}
              sx={{ color: '#ef9a9a', borderColor: 'rgba(239,154,154,0.5)',
                '&:hover': { borderColor: '#ef9a9a', bgcolor: 'rgba(239,154,154,0.08)' } }}>
              Rensa avslutade ({projects.filter(p => p.status === 'avslutad').length})
            </Button>
          )}
          <Button variant="outlined" startIcon={<UploadFileIcon />}
            onClick={openImport}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
            Importera från Milltime
          </Button>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => { setForm({ ...EMPTY_FORM, ownerId: currentUser.uid }); setDialog(true); }}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            Nytt uppdrag
          </Button>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Totalt',     value: totalCount,  color: '#FF6B00' },
            { label: 'Aktiva',     value: activeCount, color: '#1B5E20' },
            { label: 'Offertfas',  value: offertCount, color: '#E65100' },
          ].map((s, i) => (
            <Grid item xs={4} key={i}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: s.color }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Uppdragsnummer..."
            value={filterNumber} onChange={e => setFilterNumber(e.target.value)}
            sx={{ width: 170, fontFamily: 'monospace' }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            )}} />
          <TextField size="small" placeholder="Sök namn eller kund..."
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16 }} />
              </InputAdornment>
            )}} />
          <TextField select size="small" value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)} sx={{ minWidth: 150 }} label="Status">
            <MenuItem value="">Alla statusar</MenuItem>
            {Object.entries(STATUS_META).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" value={filterDept}
            onChange={e => setFilterDept(e.target.value)} sx={{ minWidth: 160 }} label="Affärsområde">
            <MenuItem value="">Alla AO</MenuItem>
            {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={filterRef}
            onChange={e => setFilterRef(e.target.value)} sx={{ minWidth: 160 }} label="Referensuppdrag">
            <MenuItem value="">Alla uppdrag</MenuItem>
            <MenuItem value="ja">Referensuppdrag</MenuItem>
            <MenuItem value="nej">Ej referensuppdrag</MenuItem>
          </TextField>
        </Box>

        {/* Selection action bar (admin) */}
        {selectMode && (
          <Paper sx={{ mb: 1.5, px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5,
            bgcolor: '#FFF3E0', border: '1px solid', borderColor: '#FFCC80' }}>
            <Button size="small" onClick={() => {
              const allShown = filtered.length > 0 && filtered.every(p => selectedIds.has(p.id));
              setSelectedIds(prev => {
                const next = new Set(prev);
                if (allShown) filtered.forEach(p => next.delete(p.id));
                else filtered.forEach(p => next.add(p.id));
                return next;
              });
            }}
              sx={{ color: '#E65100', textTransform: 'none', fontWeight: 600 }}>
              {filtered.length > 0 && filtered.every(p => selectedIds.has(p.id)) ? 'Avmarkera alla' : 'Markera alla'}
            </Button>
            <Typography variant="body2" sx={{ color: '#E65100', fontWeight: 600 }}>
              {selectedIds.size} markerade
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" color="error" size="small" startIcon={<DeleteIcon />}
              disabled={selectedIds.size === 0}
              onClick={() => setDeleteSelectedDialog(true)}>
              Radera markerade
            </Button>
          </Paper>
        )}

        {/* Sort header */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pb: 0.5, gap: 0 }}>
          {selectMode && <Box sx={{ width: 40, flexShrink: 0 }} />}
          <Box sx={{ width: 100, flexShrink: 0 }}>
            <Button size="small" onClick={() => handleSort('projectNumber')}
              sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase',
                letterSpacing: 0.8, minWidth: 0, p: 0.5,
                fontWeight: sortField === 'projectNumber' ? 'bold' : 'normal' }}>
              Nummer <SortIcon field="projectNumber" />
            </Button>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Button size="small" onClick={() => handleSort('name')}
              sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase',
                letterSpacing: 0.8, minWidth: 0, p: 0.5,
                fontWeight: sortField === 'name' ? 'bold' : 'normal' }}>
              Projektnamn <SortIcon field="name" />
            </Button>
          </Box>
          <Box sx={{ width: 88, flexShrink: 0, textAlign: 'right' }}>
            <Button size="small" onClick={() => handleSort('isReferenceProject')}
              sx={{ fontSize: '0.7rem', color: 'text.secondary', textTransform: 'uppercase',
                letterSpacing: 0.8, minWidth: 0, p: 0.5,
                fontWeight: sortField === 'isReferenceProject' ? 'bold' : 'normal' }}>
              Referens <SortIcon field="isReferenceProject" />
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <FolderOpenIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {projects.length === 0
                ? 'Inga uppdrag ännu. Skapa det första!'
                : 'Inga träffar.'}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filtered.map(project => {
              const st = STATUS_META[project.status] || STATUS_META.offertfas;
              const company = companies.find(c => c.id === project.clientId);
              const owner = users.find(u => u.id === project.ownerId);
              const projDepts = departments.filter(d =>
                (project.departmentIds || []).includes(d.id)
              );
              const isSelected = selectedIds.has(project.id);
              return (
                <Paper key={project.id} sx={{
                  pl: 0, pr: 2, py: 1.5, cursor: 'pointer',
                  borderLeft: `4px solid ${st.color}`,
                  '&:hover': { boxShadow: 3 },
                  display: 'flex', alignItems: 'center', gap: 0,
                  ...(isSelected && { bgcolor: '#FFF3E0', outline: '2px solid #FF6B00' })
                }}
                  onClick={() => selectMode
                    ? toggleSelected(project.id)
                    : navigate(`/projects/${project.id}`)}>

                  {/* Checkbox (markeringsläge) */}
                  {selectMode && (
                    <Box sx={{ width: 40, flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <input type="checkbox" checked={isSelected} readOnly
                        style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#FF6B00' }} />
                    </Box>
                  )}

                  {/* Nummerspalten */}
                  <Box sx={{ width: 108, flexShrink: 0, pl: 2, pr: 1.5,
                    borderRight: '1px solid', borderColor: 'divider',
                    display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" fontFamily="monospace"
                      fontWeight={600} sx={{ color: '#444', fontSize: '0.82rem' }}>
                      {project.projectNumber || '–'}
                    </Typography>
                  </Box>

                  {/* Huvudinnehåll */}
                  <Box sx={{ flex: 1, minWidth: 0, pl: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                      <Typography fontWeight={600} noWrap>{project.name}</Typography>
                      {projDepts.map(d => (
                        <Tooltip key={d.id} title={d.name}>
                          <Chip label={deptCode(d)} size="small"
                            sx={{ fontSize: '0.6rem', height: 18,
                              bgcolor: 'text.primary', color: 'background.paper' }} />
                        </Tooltip>
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {company && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <BusinessIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            {company.name}
                          </Typography>
                        </Box>
                      )}
                      {owner && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                          <PersonIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            {owner.displayName || owner.email}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Referensuppdrag */}
                  <Box sx={{ width: 88, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {project.isReferenceProject && (
                      <Tooltip title="Referensuppdrag">
                        <Chip
                          icon={<StarIcon sx={{ fontSize: '0.75rem !important', color: '#FF6B00 !important' }} />}
                          label="Referens"
                          size="small"
                          sx={{ fontSize: '0.6rem', height: 20,
                            bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600,
                            '& .MuiChip-icon': { ml: '4px' } }}
                        />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Status */}
                  <Box sx={{ flexShrink: 0 }}>
                    <Chip label={st.label} size="small"
                      sx={{ fontSize: '0.65rem', height: 20,
                        bgcolor: st.bg, color: st.color, fontWeight: 600 }} />
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Container>

      {/* ── Nytt uppdrag dialog ── */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FolderOpenIcon sx={{ color: '#FF6B00' }} />
            <Typography fontWeight="bold">Nytt uppdrag</Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Projektnamn *" size="small"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Projektnummer (Milltime-nummer)" size="small"
                value={form.projectNumber}
                onChange={e => setForm(f => ({ ...f, projectNumber: e.target.value }))} />
            </Grid>
            <Grid item xs={10}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField select fullWidth label="Kund/företag" size="small"
                  value={form.clientId}
                  onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}>
                  <MenuItem value="">– Välj företag –</MenuItem>
                  {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
                <Tooltip title="Skapa nytt företag">
                  <IconButton size="small" onClick={() => setNewCompanyDialog(true)}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mt: 0.3 }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Status" size="small"
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Ägare" size="small"
                value={form.ownerId}
                onChange={e => setForm(f => ({ ...f, ownerId: e.target.value }))}>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.displayName || u.email}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Startdatum" size="small"
                type="date" InputLabelProps={{ shrink: true }}
                value={form.startDate}
                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Slutdatum" size="small"
                type="date" InputLabelProps={{ shrink: true }}
                value={form.endDate}
                onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold"
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                Affärsområden
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.5 }}>
                {departments.map(d => {
                  const sel = (form.departmentIds || []).includes(d.id);
                  return (
                    <Tooltip key={d.id} title={d.name}>
                    <Chip label={deptCode(d)} size="small"
                      onClick={() => setForm(f => ({
                        ...f,
                        departmentIds: sel
                          ? f.departmentIds.filter(id => id !== d.id)
                          : [...(f.departmentIds || []), d.id]
                      }))}
                      variant={sel ? 'filled' : 'outlined'}
                      sx={{ bgcolor: sel ? '#1a1a1a' : 'transparent',
                        color: sel ? 'white' : 'text.primary', cursor: 'pointer' }} />
                    </Tooltip>
                  );
                })}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Beskrivning" size="small" multiline rows={3}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={saving || !form.name.trim()}
            sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
            {saving ? 'Skapar...' : 'Skapa uppdrag'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: skapa nytt företag */}
      <Dialog open={newCompanyDialog} onClose={() => setNewCompanyDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nytt företag</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth label="Företagsnamn" size="small" sx={{ mt: 1 }}
            value={newCompanyName}
            onChange={e => setNewCompanyName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreateCompany()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCompanyDialog(false)}>Avbryt</Button>
          <Button variant="contained" onClick={handleCreateCompany}
            disabled={!newCompanyName.trim()}>
            Skapa
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Rensa avslutade dialog ── */}
      <Dialog open={bulkDeleteDialog} onClose={() => !bulkDeleting && setBulkDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Radera avslutade uppdrag?</DialogTitle>
        <DialogContent>
          <Typography>
            {projects.filter(p => p.status === 'avslutad').length} uppdrag med status <strong>Avslutat</strong> kommer att raderas permanent. Det går inte att ångra.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBulkDeleteDialog(false)} disabled={bulkDeleting}>Avbryt</Button>
          <Button variant="contained" color="error" onClick={handleBulkDeleteAvslutade}
            disabled={bulkDeleting}
            startIcon={bulkDeleting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DeleteSweepIcon />}>
            {bulkDeleting ? 'Raderar...' : 'Radera alla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Radera markerade dialog ── */}
      <Dialog open={deleteSelectedDialog} onClose={() => !deletingSelected && setDeleteSelectedDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Radera markerade uppdrag?</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{selectedIds.size}</strong> uppdrag kommer att raderas permanent. Det går inte att ångra.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteSelectedDialog(false)} disabled={deletingSelected}>Avbryt</Button>
          <Button variant="contained" color="error" onClick={handleDeleteSelected}
            disabled={deletingSelected}
            startIcon={deletingSelected ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <DeleteIcon />}>
            {deletingSelected ? 'Raderar...' : `Radera ${selectedIds.size}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Importera från Milltime dialog ── */}
      <Dialog open={importDialog} onClose={() => importStep < 2 && setImportDialog(false)}
        maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <UploadFileIcon sx={{ color: '#FF6B00' }} />
            <Box>
              <Typography fontWeight="bold">Importera uppdrag från Milltime</Typography>
              <Typography variant="caption" color="text.secondary">
                {importStep === 0 && 'Välj en Millnet-projektexport (.xlsx)'}
                {importStep === 1 && `${importRows.length} projekt hittades – välj vad som ska importeras`}
                {importStep === 2 && 'Importerar...'}
                {importStep === 3 && 'Import klar!'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ p: 0 }}>

          {/* Step 0: File picker */}
          {importStep === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <UploadFileIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Exportera projektlistan från Millnet och ladda upp filen här.
                Filen ska ha kolumnerna: Projektnummer, Projekt, Kund, Projektledare, Resultatenhet.
                Kolumnordningen spelar ingen roll – de matchas på rubriknamn.
              </Typography>
              {importError && (
                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}
                  onClose={() => setImportError('')}>
                  {importError}
                </Alert>
              )}
              <Button variant="contained" component="label" size="large"
                startIcon={<UploadFileIcon />}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
                Välj fil (.xlsx, .xls eller .csv)
                <input type="file" hidden accept=".xlsx,.xls,.csv"
                  onChange={handleImportFile} />
              </Button>
            </Box>
          )}

          {/* Step 1: Preview */}
          {importStep === 1 && (
            <Box>
              {/* Summary bar */}
              <Box sx={{ px: 3, py: 2, bgcolor: 'yta.rubrik',
                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                borderBottom: '1px solid', borderColor: 'divider' }}>
                <Chip label={`${newCount} nya`} size="small"
                  sx={{ bgcolor: '#E8F5E9', color: '#1B5E20', fontWeight: 600 }} />
                <Chip label={`${existsCount} finns redan`} size="small"
                  sx={{ bgcolor: '#ECEFF1', color: '#37474F', fontWeight: 600 }} />
                <Chip label={`${includedCount} markerade för import`} size="small"
                  sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }} />
                {unknownCompanies.length > 0 && (
                  <Chip label={`${unknownCompanies.length} nya kunder skapas`} size="small"
                    sx={{ bgcolor: '#E3F2FD', color: '#1565C0', fontWeight: 600 }} />
                )}
                <Chip
                  label="Uppdatera befintliga"
                  size="small"
                  variant={importUpdateExisting ? 'filled' : 'outlined'}
                  onClick={toggleUpdateExisting}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: importUpdateExisting ? '#4A148C' : 'transparent',
                    color: importUpdateExisting ? 'white' : 'text.primary',
                    borderColor: '#4A148C',
                  }}
                />
                <Box sx={{ flex: 1 }} />
                {/* Filter toggles */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {[['all','Alla'], ['new','Bara nya'], ['exists','Finns redan']].map(([v, l]) => (
                    <Chip key={v} label={l} size="small" variant={importFilter === v ? 'filled' : 'outlined'}
                      onClick={() => setImportFilter(v)}
                      sx={{ cursor: 'pointer',
                        bgcolor: importFilter === v ? '#1a1a1a' : 'transparent',
                        color: importFilter === v ? 'white' : 'text.primary' }} />
                  ))}
                </Box>
              </Box>

              {unknownCompanies.length > 0 && (
                <Alert severity="info" sx={{ mx: 3, mt: 2 }}>
                  Följande kunder finns inte i CRM och skapas automatiskt:{' '}
                  <strong>{unknownCompanies.join(', ')}</strong>
                </Alert>
              )}

              <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: 'yta.rubrik' } }}>
                      <TableCell padding="checkbox" />
                      <TableCell>Nummer</TableCell>
                      <TableCell>Projektnamn</TableCell>
                      <TableCell>Kund</TableCell>
                      <TableCell>Projektledare</TableCell>
                      <TableCell>AO</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRows.map((r, i) => {
                      const origIdx = importRows.indexOf(r);
                      return (
                        <TableRow key={i}
                          sx={{ opacity: r.exists && !r.include ? 0.45 : 1,
                            bgcolor: r.include && !r.exists ? '#F9FBF9' : 'inherit' }}>
                          <TableCell padding="checkbox">
                            <input type="checkbox" checked={r.include}
                              onChange={() => toggleInclude(origIdx)} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" fontFamily="monospace">
                              {r.projectNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                              {r.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {r.clientName ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" noWrap sx={{ maxWidth: 140 }}>
                                  {r.clientName}
                                </Typography>
                                {!r.matchedCompany && (
                                  <Chip label="Ny" size="small"
                                    sx={{ height: 16, fontSize: '0.6rem',
                                      bgcolor: '#E3F2FD', color: '#1565C0' }} />
                                )}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled">–</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption"
                              sx={{ color: r.matchedUser ? 'text.primary' : 'text.disabled' }}>
                              {r.ownerName || '–'}
                              {!r.matchedUser && r.ownerName && ' *'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {r.resultatenhet && (
                              <Chip label={r.resultatenhet} size="small"
                                sx={{ height: 18, fontSize: '0.6rem',
                                  bgcolor: r.matchedDept ? '#1a1a1a' : '#eee',
                                  color: r.matchedDept ? 'white' : 'text.secondary' }} />
                            )}
                          </TableCell>
                          <TableCell>
                            {r.exists ? (
                              importUpdateExisting && r.include ? (
                                <Chip label="Uppdateras" size="small"
                                  sx={{ height: 18, fontSize: '0.6rem',
                                    bgcolor: '#EDE7F6', color: '#4A148C' }} />
                              ) : (
                                <Chip label="Finns redan" size="small"
                                  sx={{ height: 18, fontSize: '0.6rem',
                                    bgcolor: '#ECEFF1', color: '#37474F' }} />
                              )
                            ) : (
                              <Chip label="Ny" size="small"
                                sx={{ height: 18, fontSize: '0.6rem',
                                  bgcolor: '#E8F5E9', color: '#1B5E20' }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              <Box sx={{ px: 3, py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  * Projektledare som inte hittas som användare i Lagbasn tilldelas dig som ägare.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Step 2: Importing */}
          {importStep === 2 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={48} sx={{ color: '#FF6B00', mb: 2 }} />
              <Typography fontWeight="bold" sx={{ mb: 1 }}>Importerar projekt...</Typography>
              <LinearProgress variant="determinate" value={importProgress}
                sx={{ mx: 'auto', maxWidth: 400, borderRadius: 4,
                  '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {importProgress}%
              </Typography>
            </Box>
          )}

          {/* Step 3: Done */}
          {importStep === 3 && importResult && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ mb: 2 }}>✅</Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Import klar!</Typography>
              <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 1, textAlign: 'left' }}>
                {importResult.created > 0 && (
                  <Typography>
                    <strong>{importResult.created}</strong> uppdrag skapades
                  </Typography>
                )}
                {importResult.updated > 0 && (
                  <Typography sx={{ color: '#4A148C' }}>
                    <strong>{importResult.updated}</strong> uppdrag uppdaterades
                  </Typography>
                )}
                {importResult.skipped > 0 && (
                  <Typography color="text.secondary">
                    <strong>{importResult.skipped}</strong> hoppades över (finns redan)
                  </Typography>
                )}
                {importResult.newCompanies > 0 && (
                  <Typography>
                    <strong>{importResult.newCompanies}</strong> nya kunder skapades i CRM
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          {importStep === 0 && (
            <Button onClick={() => setImportDialog(false)}>Avbryt</Button>
          )}
          {importStep === 1 && (
            <>
              <Button onClick={() => setImportStep(0)}>Tillbaka</Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={() => setImportDialog(false)}>Avbryt</Button>
              <Button variant="contained" onClick={handleExecuteImport}
                disabled={includedCount === 0}
                sx={{ bgcolor: '#FF6B00', '&:hover': { bgcolor: '#e55a00' } }}>
                Importera {includedCount} uppdrag
              </Button>
            </>
          )}
          {importStep === 3 && (
            <Button variant="contained" onClick={() => setImportDialog(false)}>
              Stäng
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
